/**
 * MobileOrdersView — the redesigned mobile Orders screen (Trioty Orders Mobile).
 *
 * Self-contained: owns the Active/Needs-action/All tabs, the persisted Display
 * prefs (group / sort / row-detail fields), and the Filter state, and fetches
 * via useOrdersQuery. Rows use OUR status ring. Tapping a row opens a two-tier
 * detail: a quick bottom-sheet → "View full order" opens the existing
 * OrderDetailModal (full functionality) via `onOpenFull`.
 *
 * Features with no backing data/endpoint (New order, Export CSV, Select orders,
 * Channel group/filter) are rendered with a "Not supported" badge for review.
 */

import { Fragment, useCallback, useMemo, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrderStatus, SellerOrderListItem } from "@/lib/api/types";
import { useThemeStore } from "@/shared/theme/useTheme";
import { useOrdersQuery, type OrdersQueryPaymentMethod } from "@/modules/orders/services/useOrdersQuery";
import { useGovernoratesQuery } from "@/modules/settings/services/useGovernoratesQuery";
import { getMoneyState } from "@/modules/orders/lib/order-money";
import { relativeAgo } from "@/modules/orders/lib/order-status-display";
import { fulVisual, StatusRing } from "@/modules/orders/components/order-row/order-visuals";
import {
  buildReasonGroups,
  attentionCount,
  waitingOnBuyerCount,
  type StatusCountMap,
} from "@/modules/orders/lib/orders-attention-model";
import { useOrderStatusCountsQuery } from "@/modules/orders/services/useOrderStatusCountsQuery";
import {
  DATE_BUCKET_LABEL,
  STATUS_GROUP_LABEL,
  STATUS_GROUP_ORDER,
  dateBounds,
  dateBucket,
  inTab,
  isAttention,
  isWaitingOnBuyer,
  sortComparator,
  statusGroup,
  tabStatuses,
  type OrdersFilterState,
  type OrdersTab,
  type PaymentKind,
  type StatusGroup,
} from "./orders-mobile-model";
import { useOrdersDisplayPrefs } from "./useOrdersDisplayPrefs";
import { OrdersDisplaySheet } from "./OrdersDisplaySheet";
import { OrdersFilterSheet, EMPTY_ORDERS_FILTERS } from "./OrdersFilterSheet";
import { OrderQuickSheet } from "./OrderQuickSheet";
import { MobileHeaderNotificationBell } from "@/modules/notifications/components/MobileHeaderNotificationBell";

type Props = {
  storeSlug: string;
  /** Opens the full OrderDetailModal (existing drawer) for the given order id. */
  onOpenFull: (orderId: string) => void;
  /** Quick-action handlers, reused from the page for the decline-reason / ETA flow. */
  onQuickAction: (order: SellerOrderListItem, targetStatus: OrderStatus) => void;
  onOpenQuickActionSheet: (order: SellerOrderListItem) => void;
};


/** Derives the buyer's payment kind for client-side filtering / labels. */
function paymentKindOf(o: SellerOrderListItem): PaymentKind {
  if (o.paymentMethod === "COD") return "cod";
  if (o.paidAmount > 0 && o.paidAmount < o.requiredAmount) return "deposit";
  return "paid";
}

export function MobileOrdersView({
  storeSlug,
  onOpenFull,
  onQuickAction,
  onOpenQuickActionSheet,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const isDark = useThemeStore((s) => s.resolvedDark);
  const { prefs, setGroup, setSort, toggleProp, resetProps } = useOrdersDisplayPrefs(storeSlug);

  const [tab, setTab] = useState<OrdersTab>("active");
  const [filters, setFilters] = useState<OrdersFilterState>(EMPTY_ORDERS_FILTERS);
  const [limit, setLimit] = useState(60);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Changing the tab or filters is a fresh result set — reset the page size.
  const changeTab = useCallback((next: OrdersTab) => {
    setTab(next);
    setLimit(60);
  }, []);
  const changeFilters = useCallback((next: OrdersFilterState) => {
    setFilters(next);
    setLimit(60);
  }, []);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sheetOrderId, setSheetOrderId] = useState<string | null>(null);

  // Backend status = intersection of the tab's statuses and any selected
  // status-group filter; empty selection → the tab's statuses.
  const statusParam = useMemo<OrderStatus[] | undefined>(() => {
    const base = tabStatuses(tab);
    if (filters.status.length === 0) return base;
    const wanted = new Set(filters.status);
    const fromFilter = (base ?? ALL_STATUSES).filter((s) => wanted.has(statusGroup(s)));
    return fromFilter;
  }, [tab, filters.status]);

  // Payment: pass to the backend only when exactly one of cod/paid is selected
  // (deposit is derived client-side), else fetch all and filter locally.
  const backendPayment = useMemo<OrdersQueryPaymentMethod | undefined>(() => {
    const p = filters.payment;
    if (p.length === 1 && p[0] === "cod") return "COD";
    if (p.length === 1 && p[0] === "paid") return "EXTERNAL_ONLINE";
    return undefined;
  }, [filters.payment]);

  const backendSort = prefs.sort === "oldest" ? "oldest" : "newest";

  const { data, isLoading, isFetching, error } = useOrdersQuery({
    storeSlug,
    limit,
    status: statusParam,
    paymentMethod: backendPayment,
    governorateIds: filters.governorate.length ? filters.governorate : undefined,
    sort: backendSort,
    ...dateBounds(filters.date),
  });

  const allOrders = useMemo(() => data?.items ?? [], [data?.items]);
  // Backend has more rows than we've loaded → offer "Load more".
  const canLoadMore = allOrders.length < (data?.total ?? 0);

  // Governorate names for the "Location" row-detail field (address has no city).
  const { data: governorates } = useGovernoratesQuery({ storeSlug });
  const govNameById = useMemo(() => {
    const map = new Map<string, string>();
    (governorates ?? []).forEach((g) =>
      map.set(g.id, locale === "ar" ? g.nameAr : g.nameEn ?? g.nameAr),
    );
    return map;
  }, [governorates, locale]);

  // Client-side payment + shipment filtering (deposit/multi-select + shipment
  // status live on the list item). `inTab` keeps drafts to the "All" tab.
  const filtered = useMemo(() => {
    let pool = allOrders.filter((o) => inTab(o.status, tab));
    if (filters.payment.length) {
      const set = new Set(filters.payment);
      pool = pool.filter((o) => set.has(paymentKindOf(o)));
    }
    if (filters.shipment.length) {
      const set = new Set(filters.shipment);
      pool = pool.filter((o) => o.shipment != null && set.has(o.shipment.status));
    }
    return pool.slice().sort(sortComparator(prefs.sort));
  }, [allOrders, tab, filters.payment, filters.shipment, prefs.sort]);

  const activeFilterCount =
    filters.status.length +
    filters.payment.length +
    filters.governorate.length +
    filters.shipment.length +
    (filters.date !== "all" ? 1 : 0);
  const hasFilters = activeFilterCount > 0;

  // Per-status server counts, scoped to the filters the backend can represent
  // (date range, governorate, single COD/paid). The mobile filter set also has
  // client-only refinements (deposit / multi-select payment, shipment status,
  // status-group) the facet can't express — when one of those is active we fall
  // back to the loaded-rows counts so the numbers still match the visible set.
  const countsFilters = useMemo(
    () => ({
      ...dateBounds(filters.date),
      governorateIds: filters.governorate.length ? filters.governorate : undefined,
      paymentMethod: backendPayment,
    }),
    [filters.date, filters.governorate, backendPayment],
  );
  const filtersServerRepresentable =
    filters.status.length === 0 &&
    filters.shipment.length === 0 &&
    (filters.payment.length === 0 || backendPayment !== undefined);
  const { data: statusCountsData } = useOrderStatusCountsQuery({
    storeSlug,
    filters: countsFilters,
  });
  const serverCounts: StatusCountMap | null = filtersServerRepresentable
    ? statusCountsData?.counts ?? null
    : null;

  // Three sections: "Needs attention" (seller must act) grouped by next action,
  // urgency-ordered oldest-first; "Waiting on buyer" (no seller action) as a
  // flat quiet list; and the rest ("Completed") grouped by the Display "Group by".
  const attentionGroups = useMemo(
    () => attentionBuiltGroups(filtered.filter((o) => isAttention(o.status)), serverCounts),
    [filtered, serverCounts],
  );
  const waitingGroups = useMemo<BuiltGroup[]>(() => {
    const rows = filtered.filter((o) => isWaitingOnBuyer(o.status));
    return rows.length
      ? [{ key: "waiting", labelKey: "", showHeader: false, sampleStatus: null, rows }]
      : [];
  }, [filtered]);
  const restGroups = useMemo(
    () =>
      buildGroups(
        filtered.filter((o) => !isAttention(o.status) && !isWaitingOnBuyer(o.status)),
        prefs.group,
      ),
    [filtered, prefs.group],
  );
  const shown = filtered.length;

  // Section header totals (server truth when unfiltered, else loaded counts).
  const attentionTotal = serverCounts
    ? attentionCount(serverCounts)
    : attentionGroups.reduce((n, g) => n + g.rows.length, 0);
  const waitingTotal = serverCounts
    ? waitingOnBuyerCount(serverCounts)
    : waitingGroups.reduce((n, g) => n + g.rows.length, 0);

  const sheetOrder = sheetOrderId
    ? allOrders.find((o) => o.id === sheetOrderId) ?? null
    : null;

  // Renders a list of built groups (collapsible header + rows). Shared by the
  // "Needs attention" (reason) and "Completed" (status) sections.
  const renderGroups = (list: BuiltGroup[]) =>
    list.map((g, gi) => {
      const isCollapsed = !!collapsed[g.key];
      // Attention headers get a count pill in the group's status colour, matching
      // the rows' rings (shared fulVisual palette).
      const headColor =
        g.attention && g.sampleStatus ? fulVisual(g.sampleStatus, isDark).color : null;
      // Server-truth count when available, else the loaded rows in this group.
      const count = g.count ?? g.rows.length;
      return (
        <div
          key={g.key}
          className={gi === 0 ? "" : "border-t border-[color:var(--hair)]"}
        >
          {g.showHeader ? (
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
              className="flex w-full items-center gap-2.5 px-1 py-3 text-start transition-colors hover:bg-[color:var(--soft)]"
            >
              <ChevronIcon rotated={isCollapsed} />
              {g.sampleStatus ? <RingFor status={g.sampleStatus} isDark={isDark} /> : null}
              <span className="text-[13.5px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
                {t(g.labelKey)}
              </span>
              {headColor ? (
                <span
                  className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold"
                  style={{
                    background: `color-mix(in srgb, ${headColor} 15%, transparent)`,
                    color: headColor,
                  }}
                >
                  {count}
                </span>
              ) : (
                <span className="text-[12.5px] font-semibold text-[color:var(--text-muted)]">
                  {count}
                </span>
              )}
            </button>
          ) : null}
          {!isCollapsed
            ? g.rows.map((o, i) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  locale={locale}
                  isDark={isDark}
                  props={prefs.props}
                  govNameById={govNameById}
                  last={i === g.rows.length - 1}
                  onOpen={() => setSheetOrderId(o.id)}
                />
              ))
            : null}
        </div>
      );
    });

  return (
    <div className="flex flex-col">
      {/* Fixed top region: title + actions + tabs stick while the list scrolls
          under them (single hairline separates them from the list). */}
      <div
        className="sticky top-0 z-20 -mx-4 border-b border-[color:var(--hair)] bg-[color:var(--canvas)] px-4"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)" }}
      >
        {/* Header row: title + New order (NS) + More */}
        <div className="flex items-center justify-between gap-2 px-1 pb-1 pt-1">
        <h1 className="text-[28px] font-extrabold tracking-[-0.025em] text-[color:var(--text-primary)]">
          {t(keys.orders.title)}
        </h1>
        <div className="flex items-center gap-1 rounded-full bg-[color:var(--soft2)] p-[5px]">
          <MobileHeaderNotificationBell triggerClassName="relative flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]" />
          <span className="h-[17px] w-px bg-[color:var(--border-strong)]" />
          <Menu as="div" className="relative flex">
            <MenuButton
              aria-label={t(keys.orders.mMore)}
              className="flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
            >
              <DotsIcon />
            </MenuButton>
            <MenuItems
              anchor="bottom end"
              className="z-50 mt-1 w-[212px] rounded-[14px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-1.5 shadow-[0_20px_54px_rgba(0,0,0,0.28)] focus:outline-none"
            >
              <MenuItem>
                <button
                  type="button"
                  onClick={() => setDisplayOpen(true)}
                  className="flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2.5 text-start text-[14px] font-medium text-[color:var(--text-primary)] data-[focus]:bg-[color:var(--soft)]"
                >
                  <SlidersIcon />
                  {t(keys.orders.mDisplay)}
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      </div>

        {/* Tabs + filter button */}
        <div className="flex items-center gap-1.5 px-1 pb-2 pt-1">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {(["active", "all"] as OrdersTab[]).map((tk) => {
            const on = tk === tab;
            return (
              <button
                key={tk}
                type="button"
                onClick={() => changeTab(tk)}
                className="whitespace-nowrap rounded-full px-3 py-[5px] text-[13px] font-semibold transition-colors"
                style={{
                  background: on ? "var(--soft2)" : "transparent",
                  color: on ? "var(--text-primary)" : "var(--text-muted)",
                }}
              >
                {t(tk === "active" ? keys.orders.tabActive : keys.orders.tabAll)}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setFilterOpen(true)}
          aria-label={t(keys.orders.filterTitle)}
          className="relative flex h-[34px] w-[34px] items-center justify-center rounded-[10px] transition-colors hover:bg-[color:var(--soft)]"
          style={{ color: hasFilters ? "var(--accent-500)" : "var(--text-muted)" }}
        >
          <FilterIcon />
          {hasFilters ? (
            <span
              className="absolute -end-0.5 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1 text-[9.5px] font-bold"
              style={{ background: "var(--accent-500)", color: "var(--on-accent)" }}
            >
              {activeFilterCount}
            </span>
          ) : null}
        </button>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="px-1 py-10 text-center text-[13px] text-[color:var(--text-muted)]">
          {t(keys.common.loading)}
        </div>
      ) : error ? (
        <div className="px-1 py-10 text-center text-[13px] text-[color:var(--text-muted)]">
          {t(keys.orders.loadError)}
        </div>
      ) : shown === 0 ? (
        <EmptyState
          hasFilters={hasFilters}
          onClear={() => changeFilters(EMPTY_ORDERS_FILTERS)}
          t={t}
        />
      ) : (
        <div className="pt-1">
          {(() => {
            const sections: Array<{
              key: string;
              label: string;
              groups: BuiltGroup[];
              count?: number;
            }> = [
              {
                key: "attention",
                label: keys.orders.attentionSectionLabel,
                groups: attentionGroups,
                count: attentionTotal,
              },
              {
                key: "waiting",
                label: keys.orders.waitingOnBuyerSectionLabel,
                groups: waitingGroups,
                count: waitingTotal,
              },
              { key: "rest", label: keys.orders.completedSectionLabel, groups: restGroups },
            ].filter((s) => s.groups.length > 0);
            const showLabels = sections.length > 1;
            return sections.map((s) => (
              <Fragment key={s.key}>
                {showLabels ? <SectionLabel count={s.count}>{t(s.label)}</SectionLabel> : null}
                {renderGroups(s.groups)}
              </Fragment>
            ));
          })()}
          {canLoadMore ? (
            <div className="flex justify-center px-1 pb-2 pt-5">
              <button
                type="button"
                onClick={() => setLimit((l) => l + 60)}
                disabled={isFetching}
                className="inline-flex items-center gap-2 rounded-[11px] bg-[color:var(--soft2)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--text-primary)] transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {isFetching ? (
                  <svg aria-hidden="true" className="h-[15px] w-[15px] animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                  </svg>
                ) : null}
                {t(keys.orders.loadMore)}
              </button>
            </div>
          ) : null}
          <div className="px-1 pb-2 pt-3 text-center text-[11.5px] font-medium text-[color:var(--text-disabled)]">
            {t(keys.orders.ordersCount, { count: shown })}
          </div>
        </div>
      )}

      <OrdersDisplaySheet
        isOpen={displayOpen}
        onClose={() => setDisplayOpen(false)}
        prefs={prefs}
        onGroup={setGroup}
        onSort={setSort}
        onToggleProp={toggleProp}
        onResetProps={resetProps}
      />
      <OrdersFilterSheet
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        storeSlug={storeSlug}
        value={filters}
        onChange={changeFilters}
      />
      <OrderQuickSheet
        order={sheetOrder}
        isDark={isDark}
        locale={locale}
        onClose={() => setSheetOrderId(null)}
        onOpenFull={(id) => {
          setSheetOrderId(null);
          onOpenFull(id);
        }}
        onQuickAction={onQuickAction}
        onOpenQuickActionSheet={onOpenQuickActionSheet}
      />
    </div>
  );
}

const ALL_STATUSES: OrderStatus[] = [
  "draft",
  "paid_less_than_required",
  "pending_buyer_verification",
  "pending_buyer_payment",
  "pending_payment_verification",
  "pending_seller_confirmation",
  "confirmed",
  "preparing",
  "shipped",
  "delivered",
  "cancelled",
  "declined",
];

type BuiltGroup = {
  key: string;
  labelKey: string;
  showHeader: boolean;
  sampleStatus: OrderStatus | null;
  /** True for "Needs attention" reason groups — gives the header a stage-coloured
   *  count pill (matching the rows' ring colour). */
  attention?: boolean;
  /** Server-truth group total (store-wide). Falls back to rows.length when unset. */
  count?: number;
  rows: SellerOrderListItem[];
};

function buildGroups(
  pool: SellerOrderListItem[],
  group: "status" | "date" | "channel" | "none",
): BuiltGroup[] {
  // Channel has no backing data — fall back to a single ungrouped list.
  if (group === "none" || group === "channel") {
    return pool.length
      ? [{ key: "none", labelKey: "", showHeader: false, sampleStatus: null, rows: pool }]
      : [];
  }
  if (group === "date") {
    const now = new Date();
    const order: Array<"today" | "yesterday" | "earlier"> = ["today", "yesterday", "earlier"];
    return order
      .map((b) => ({
        key: `dt_${b}`,
        labelKey: DATE_BUCKET_LABEL[b],
        showHeader: true,
        sampleStatus: null as OrderStatus | null,
        rows: pool.filter((o) => dateBucket(o.createdAt, now) === b),
      }))
      .filter((g) => g.rows.length > 0);
  }
  return STATUS_GROUP_ORDER.map((sg) => ({
    key: sg,
    labelKey: STATUS_GROUP_LABEL[sg],
    showHeader: true,
    sampleStatus: stageSampleStatus(sg),
    rows: pool.filter((o) => statusGroup(o.status) === sg),
  })).filter((g) => g.rows.length > 0);
}

/** Reason groups for "Needs attention" as BuiltGroups, from the shared model.
 *  `counts` (per-status server truth) drives the header count when present. */
function attentionBuiltGroups(
  pool: SellerOrderListItem[],
  counts: StatusCountMap | null,
): BuiltGroup[] {
  return buildReasonGroups(pool).map((g) => ({
    key: `attn_${g.key}`,
    labelKey: g.labelKey,
    showHeader: true,
    sampleStatus: g.sampleStatus,
    attention: true,
    count: counts ? g.statuses.reduce((n, s) => n + (counts[s] ?? 0), 0) : g.orders.length,
    rows: g.orders,
  }));
}

function SectionLabel({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div className="flex items-center gap-1.5 px-1 pb-2 pt-4 text-[12px] font-bold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">
      <span>{children}</span>
      {count != null ? <span className="text-[color:var(--text-disabled)]">{count}</span> : null}
    </div>
  );
}

/** A representative OrderStatus for a group, so the header ring matches. */
function stageSampleStatus(g: StatusGroup): OrderStatus {
  switch (g) {
    case "awaiting_payment":
      return "pending_payment_verification";
    case "to_confirm":
      return "pending_seller_confirmation";
    case "preparing":
      return "preparing";
    case "in_transit":
      return "shipped";
    case "delivered":
      return "delivered";
    case "closed":
      return "cancelled";
    case "draft":
      return "draft";
  }
}

function RingFor({
  status,
  isDark,
  color,
  size = 18,
}: {
  status: OrderStatus;
  isDark: boolean;
  color?: string;
  size?: number;
}) {
  const v = fulVisual(status, isDark);
  return (
    <StatusRing color={color ?? v.color} kind={v.ring} frac={v.frac} isDark={isDark} size={size} />
  );
}

function OrderRow({
  order,
  locale,
  isDark,
  props,
  govNameById,
  last,
  onOpen,
}: {
  order: SellerOrderListItem;
  locale: string;
  isDark: boolean;
  props: Record<string, boolean>;
  govNameById: Map<string, string>;
  last: boolean;
  onOpen: () => void;
}) {
  const t = useTranslations();
  const money = getMoneyState(
    order.status,
    order.paymentMethod,
    order.paidAmount,
    order.requiredAmount,
    order.totalAmount,
  );
  const parts: string[] = [];
  if (props.customer) parts.push(order.customer.name || "—");
  if (props.items) parts.push(t(keys.orders.itemsCountSummary, { count: order.itemCount }));
  if (props.location) {
    const loc =
      (order.governorateId ? govNameById.get(order.governorateId) : null) ??
      order.address?.area ??
      null;
    if (loc) parts.push(loc);
  }
  if (props.payment) {
    const k = paymentKindOf(order);
    parts.push(
      k === "cod"
        ? t(keys.orders.paymentMethodCod)
        : k === "deposit"
          ? t(keys.orders.payDeposit)
          : t(keys.orders.payPaidOnline),
    );
  }
  const showVal = props.value;
  const showTime = props.time;
  const when = relativeAgo(order.createdAt, locale);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen();
      }}
      className="relative flex cursor-pointer items-center gap-3 px-1 py-3 transition-colors hover:bg-[color:var(--soft)]"
    >
      <RingFor status={order.status} isDark={isDark} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
          #{order.orderCode}
        </div>
        {parts.length ? (
          <div className="mt-0.5 truncate text-[12px] text-[color:var(--text-muted)]">
            {parts.join(" · ")}
          </div>
        ) : null}
      </div>
      {showVal || showTime ? (
        <div className="shrink-0 text-end">
          {showVal ? (
            <div className="text-[15px] font-extrabold tracking-[-0.01em] text-[color:var(--text-primary)]">
              {money.headlineFormatted}
            </div>
          ) : null}
          {showTime && when ? (
            <div className={`text-[11px] text-[color:var(--text-muted)] ${showVal ? "mt-0.5" : ""}`}>
              {when}
            </div>
          ) : null}
        </div>
      ) : null}
      {!last ? (
        <span className="absolute bottom-0 end-0 start-[42px] h-px bg-[color:var(--hair)]" />
      ) : null}
    </div>
  );
}

function EmptyState({
  hasFilters,
  onClear,
  t,
}: {
  hasFilters: boolean;
  onClear: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-10 py-16 text-center">
      <div className="mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-[15px] bg-[color:var(--soft)] text-[color:var(--text-muted)]">
        <ReceiptIcon />
      </div>
      <div className="text-[15px] font-semibold tracking-[-0.01em] text-[color:var(--text-secondary)]">
        {t(hasFilters ? keys.orders.emptyNoMatchTitle : keys.orders.emptyNoOrdersTitle)}
      </div>
      <div className="mt-1.5 max-w-[230px] text-[13px] leading-relaxed text-[color:var(--text-muted)]">
        {t(hasFilters ? keys.orders.emptyNoMatchSub : keys.orders.emptyNoOrdersSub)}
      </div>
      {hasFilters ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-4 rounded-[11px] bg-[color:var(--soft2)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--text-primary)]"
        >
          {t(keys.orders.clearFilters)}
        </button>
      ) : null}
    </div>
  );
}

// ─── small helpers / icons ───────────────────────────────────────────────────

const ICON = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function DotsIcon() {
  return (
    <svg {...ICON} stroke="none" fill="currentColor">
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  );
}
function SlidersIcon() {
  return (
    <svg {...ICON} className="text-[color:var(--text-muted)]">
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h7M15 18h5" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="13" cy="18" r="2" />
    </svg>
  );
}
function FilterIcon() {
  return (
    <svg {...ICON} width={18} height={18} strokeWidth={1.9}>
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}
function ChevronIcon({ rotated }: { rotated: boolean }) {
  return (
    <svg
      {...ICON}
      width={11}
      height={11}
      strokeWidth={3}
      className="shrink-0 text-[color:var(--text-muted)] transition-transform"
      style={{ transform: rotated ? "rotate(-90deg)" : "rotate(0deg)" }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
function ReceiptIcon() {
  return (
    <svg {...ICON} width={24} height={24} strokeWidth={1.7}>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 9h6M9 13h4" />
    </svg>
  );
}
